// @ts-check
import { useQuery } from '@tanstack/react-query';
import { getIsStorm } from 'banana-stand/parsers';
import { fromApiBoolean } from 'club-sauce';
import dataCenterMap from 'containers/pages/shop/ConfigPackage/dataCenterMap';
import api from 'modules/queries/api';
import displayName from 'utility/assetDisplayNames';
import { getType as getBackupType } from 'utility/backups';
import createAssetType from 'utility/createAssetType';
import formatBytes from 'utility/formatBytes';
import { getBeyondHostingPowerStatus, isUnknown } from 'utility/powerStatus';

/**
 * removes square backets and contents from osName (for example:
 * [App:WordPress Optimized])
 * @param {string} osName */
export const stripOsName = (osName) => {
	return osName.replace(/\s*\[.*?\]\s*/g, '');
};

/**
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * @param {boolean} [param0.isBeyondHosting]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails']} [param0.nocworxRemoteDetails]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 * */
const getOS = ({
	assetType,
	featureDetails,
	nocworxRemoteDetails,
	isBeyondHosting,
	machine,
}) => {
	if (isBeyondHosting) {
		return nocworxRemoteDetails?.os?.identity || '';
	}

	if (!featureDetails) {
		return '';
	}

	if (assetType === 'open stack') {
		return (
			Object.values(featureDetails)?.find((option) =>
				fromApiBoolean(option.capabilities.nocworxImage),
			)?.description || ''
		);
	}

	if (assetType === 'bare metal') {
		return machine?.template_description || '';
	}

	return (
		featureDetails.Template?.description ||
		featureDetails.centos?.description ||
		featureDetails.winos?.description ||
		featureDetails.OS?.description ||
		''
	);
};

/**
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 */
const getProcessor = ({ assetType, featureDetails, machine }) => {
	if (assetType === 'open stack') {
		return Object.values(featureDetails || {}).find((option) =>
			fromApiBoolean(option.capabilities.nocworxCPU),
		)?.description;
	}

	if (machine?.cpu_model) {
		const { cpu_model: cpuModel, cpu_count: cpuCount } = machine;

		if (cpuCount === 2) return `Dual ${cpuModel}`;
		if (cpuCount === 4) return `Quad ${cpuModel}`;

		return cpuModel;
	}

	return (
		featureDetails?.ConfigId?.cpu_description ||
		featureDetails?.Processor?.description
	);
};

/**
 * @param {object} param0
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * @param {boolean} param0.isPrivateParent
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails']} [param0.nocworxRemoteDetails]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 * */
const getCores = ({
	featureDetails,
	isPrivateParent,
	nocworxRemoteDetails,
	machine,
}) => {
	if (isPrivateParent) {
		const { cpu_cores_per_socket: cores = 0, cpu_sockets: sockets = 0 } =
			featureDetails?.ConfigId || {};
		const displayedCores = cores * sockets;

		return displayedCores;
	}

	return (
		machine?.cpu_cores ||
		nocworxRemoteDetails?.server_details?.cpu ||
		featureDetails?.ConfigId?.vcpu ||
		featureDetails?.ConfigId?.cpu_cores
	);
};

/**
 * Depending on the asset, returns the processor, cores, or vCPUs.
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * @param {boolean} [param0.isBeyondHosting]
 * @param {boolean} param0.isPrivateParent
 * @param {boolean} param0.isVmware
 * @param {boolean} param0.isChild
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails']} [param0.nocworxRemoteDetails]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 * */
const getCpu = ({
	assetType,
	featureDetails,
	isBeyondHosting,
	isChild,
	isPrivateParent,
	isVmware,
	nocworxRemoteDetails,
	machine,
}) => {
	if (isBeyondHosting || isChild || isPrivateParent || assetType === 'cloud') {
		const cores = getCores({
			featureDetails,
			isPrivateParent,
			nocworxRemoteDetails,
			machine,
		});

		if (!cores) return '';

		return cores + (cores > 1 ? ' cores' : ' core');
	}

	if (
		assetType === 'open stack' ||
		assetType === 'dedicated' ||
		assetType === 'bare-metal'
	) {
		return getProcessor({ assetType, featureDetails, machine });
	}

	if (isVmware) {
		const vCpus = featureDetails?.vDCvCPU?.num_units;

		return vCpus ? `${vCpus} vCPU's` : '';
	}

	return '';
};

/**
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 */
const getGpu = ({ assetType, machine }) => {
	if (assetType === 'bare metal') {
		if (!machine) return '';

		const { gpu_name: gpuName, gpu_count: gpuCount } = machine;

		if (!gpuName) return '';

		if (typeof gpuCount === 'number' && gpuCount > 1) {
			return `${gpuCount} x ${gpuName}`;
		}

		return gpuName;
	}

	return '';
};

/**
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * @param {boolean} [param0.isBeyondHosting]
 * @param {boolean} param0.isPrivateParent
 * @param {boolean} param0.isChild
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails']} [param0.nocworxRemoteDetails]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 * */
const getRam = ({
	assetType,
	featureDetails,
	isBeyondHosting,
	isPrivateParent,
	isChild,
	nocworxRemoteDetails,
	machine,
}) => {
	if (isBeyondHosting && nocworxRemoteDetails?.server_details?.ram) {
		return `${nocworxRemoteDetails?.server_details?.ram}MB`;
	}

	if (assetType === 'bare metal') {
		return machine?.memory ? `${machine.memory} GB` : '';
	}

	if (!featureDetails) {
		return '';
	}

	if (isPrivateParent || isChild || assetType === 'cloud') {
		return featureDetails.ConfigId?.memory;
	}

	if (assetType === 'open stack') {
		return (
			Object.values(featureDetails).find((option) =>
				fromApiBoolean(option.capabilities.nocworxMemory),
			)?.description || ''
		);
	}

	if (assetType === 'dedicated') {
		return featureDetails.RAM?.description || '';
	}

	return '';
};

/** @param {AssetDetails['ram']} ram */
const getRamFormatted = (ram) => {
	if (!ram) {
		return '';
	}

	if (typeof ram === 'number') {
		return formatBytes(ram * 1e6).withUnit;
	}

	return ram;
};

/**
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * @param {boolean} [param0.isBeyondHosting]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['nocworxRemoteDetails']} [param0.nocworxRemoteDetails]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 * */
const getPrimaryStorage = ({
	assetType,
	featureDetails,
	isBeyondHosting,
	nocworxRemoteDetails,
	machine,
}) => {
	if (isBeyondHosting) {
		if (!nocworxRemoteDetails?.server_details) {
			return '';
		}
		return `${nocworxRemoteDetails.server_details.disk_space}GB`;
	}

	if (assetType === 'bare metal') {
		if (!machine) return '';

		const { disk, disk_type: diskType } = machine;

		if (!disk) return '';

		const storageParts = [];

		if (disk) {
			storageParts.push(`${disk} GB`);
		}

		if (diskType) {
			storageParts.push(diskType);
		}

		return storageParts.join(', ');
	}

	if (!featureDetails) {
		return '';
	}

	if (assetType === 'open stack') {
		const diskOption = Object.values(featureDetails).find((option) =>
			fromApiBoolean(option.capabilities.nocworxVolumeSize),
		);
		const { num_units: diskValue, description: diskDescription } =
			diskOption || {};

		return diskValue ? `${diskValue} GB` : diskDescription || '';
	}

	const disk = featureDetails.ConfigId?.disk;

	return disk ? `${disk} GB` : '';
};

/**
 * @param {object} param0
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [param0.featureDetails]
 * */
const getHardDrives = ({ featureDetails }) => {
	if (!featureDetails) {
		return [];
	}

	const features = Object.entries(featureDetails);

	return features
		.filter(
			([key, { description }]) =>
				key.search(/HD\d/) === 0 && description !== 'No Tertiary Drive',
		)
		.map(([key, { option_key_display_text: title, ptov_id: ptovId }]) => {
			const [, childOption] =
				features.find(
					([childKey, { parent_ptov_id: parentPtovId }]) =>
						childKey.toLowerCase().includes('drivesize') &&
						parentPtovId === ptovId,
				) || [];

			const childDescription = childOption ? childOption?.description : 'None';

			return {
				title: title || key,
				description: childDescription,
			};
		});
};

/**
 * @param {object} param0
 * @param {string} param0.assetType
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 */
const getLinkSpeed = ({ assetType, machine }) => {
	if (assetType === 'bare metal') {
		if (!machine) return '';

		const { link_speed: linkSpeed } = machine;

		if (typeof linkSpeed === 'number' && linkSpeed > 0) {
			return `${linkSpeed / 1000} Gbps`;
		}

		return '';
	}

	return '';
};

/**
 * @param {object} param0
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['machine']} [param0.machine]
 */
const getProcessorSpeed = ({ machine }) => {
	if (!machine) return '';

	const { cpu_speed: cpuSpeed } = machine;

	if (typeof cpuSpeed === 'number' && cpuSpeed > 0) {
		return `${cpuSpeed / 1000} GHz`;
	}

	return '';
};

/**
 * @param {object} params
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['resourceState']} [params.resourceState]
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['diskDetails']} [params.diskDetails]
 */
const getParentDiskResources = ({
	resourceState,
	diskDetails = { allocated: 0, snapshots: 0 },
}) => {
	if (!resourceState) return null;

	const data = resourceState.find((value) => value.type === 'disk.lvm.volume');
	return data
		? {
				// for display purposes, we need to remove reserved snapshot space from the amounts
				free: Number(data.free) - diskDetails.snapshots,
				used: Number(data.used) - diskDetails.snapshots,
				total: Number(data.total) - diskDetails.snapshots,
			}
		: null;
};

/** @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI['resourceState']} resourceState */
const getParentMemoryResources = (resourceState) => {
	if (!resourceState) return null;
	return resourceState.find((value) => value.type === 'memory') || null;
};

/**
 * @param {boolean} hasAcronis
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetFeatureDetailsRawT} [featureDetails]
 * */
const getBackupPlan = (hasAcronis, featureDetails) => {
	const backupPlanRaw = featureDetails?.LiquidWebBackupPlan;
	if (hasAcronis) return 'Acronis';
	return getBackupType(backupPlanRaw?.value || '');
};

/**
 * @param {boolean} canAcronisBackup
 * @param {"Acronis" | "Daily" | "Quota" | "None"} currentBackupPlan
 * */

const getBackupsState = (canAcronisBackup, currentBackupPlan) => ({
	canAcronis: canAcronisBackup,
	currentPlan: currentBackupPlan,
	hasAcronis: currentBackupPlan === 'Acronis',
	hasBackups: currentBackupPlan !== 'None',
	hasLegacyBackups:
		currentBackupPlan === 'Daily' || currentBackupPlan === 'Quota',
});

/**
 * @typedef BackupsState
 * @property {boolean} canAcronis
 * @property {"Acronis" | "Daily" | "Quota" | "None"} currentPlan
 * @property {boolean} hasAcronis
 * @property {boolean} hasBackups
 * @property {boolean} hasLegacyBackups
 */

/**
 * @typedef ExtendedAssetDetails
 * @property {string} [acronisId]
 * @property {string} [acronisStatus]
 * @property {string} assetType
 * @property {number | null} availabilityZoneId prefer this to zone.id if referencing templates, plans, etc. that have are only available in a specific zones
 * @property {string | null} [backupPlan]
 * @property {string} [backupQuotaValue]
 * @property {BackupsState} [backupsState]
 * @property {number} bandwidthInGB
 * @property {boolean} canBackupsAndImages
 * @property {boolean} canConsole
 * @property {string} [controlPanel]
 * @property {string} [country]
 * @property {number} [cores]
 * @property {string} [cpanelLicense]
 * @property {string} [cpu]
 * @property {boolean} disableConsole
 * @property {string} displayType
 * @property {string} gpu
 * @property {{title: string, description: string}[]} hardDrives
 * @property {boolean} hasAcronis
 * @property {boolean} hasBlockStorage
 * @property {boolean} [isBareMetal]
 * @property {boolean} [isBeyondHosting]
 * @property {boolean} isMWPv1
 * @property {boolean} [isOpenStack]
 * @property {boolean} isPrivateParent
 * @property {boolean} isStorm
 * @property {boolean} isVmware
 * @property {boolean} isWindows
 * @property {string} linkSpeed
 * @property {string} [location]
 * @property {string} [managementLevel]
 * @property {string} [mesRole]
 * @property {string} [mesType]
 * @property {string} os
 * @property {string} osFormatted
 * @property {{free: number; used: number; total: number;} | null} parentDiskResources
 * @property {import('club-sauce/public/asset/index.raw').ResourceStateRawI | null} parentMemoryResources
 * @property {string} [powerStatus]
 * @property {string} primaryStorage
 * @property {string} [processor]
 * @property {string} [processorSpeed]
 * @property {string | null} [privateIp]
 * @property {string | number} [ram]
 * @property {string} ramFormatted
 * @property {string} raidLevelFormatted
 * @property {string} [zoneFormatted]
 */

/**
 * @typedef {Omit<import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI, 'powerStatus'> & ExtendedAssetDetails} AssetDetails
 * */

/**
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsResultRawI} data
 * @return {AssetDetails}
 * */
const supplementDetails = (data) => {
	const {
		altGroup,
		backupsAndStorage,
		capabilities,
		categories,
		diskDetails,
		featureDetails,
		machine,
		mesDetails,
		networkSummary,
		nocworxRemoteDetails,
		powerStatus: powerStatusRaw,
		privateParent,
		region_id: regionId,
		resourceState,
		template,
		type,
		zone,
	} = data || {};
	const { uniq_id: acronisId, status: acronisStatus } =
		backupsAndStorage?.acronis || {};

	const assetType = createAssetType(categories || []);
	const availabilityZoneId = zone?.name ? Number(zone.id) : null;
	const backupQuotaValue = featureDetails?.BackupQuota?.value;
	const bandwidthInGB = Number(featureDetails?.Bandwidth?.value.split('.')[1]);
	const canAcronisBackup = fromApiBoolean(capabilities?.canAcronisBackup);
	const canBackupsAndImages =
		fromApiBoolean(capabilities?.image) ||
		fromApiBoolean(capabilities?.provBackup) ||
		fromApiBoolean(capabilities?.canAcronisBackup);
	const canConsole =
		fromApiBoolean(capabilities?.canUseSpiceConsole) ||
		fromApiBoolean(capabilities?.canUseVNCConsole);
	const controlPanel =
		template?.control_panel ||
		featureDetails?.Template?.extra_software ||
		featureDetails?.ControlPanel?.description ||
		featureDetails?.CloudManagement?.description;
	const country = regionId ? dataCenterMap[regionId]?.country : undefined;
	const cpanelLicense = fromApiBoolean(capabilities?.cpanelLogin)
		? featureDetails?.cPanelLicenseTier?.description
		: undefined;
	const disableConsole = isUnknown(powerStatusRaw);
	const hardDrives = getHardDrives({
		featureDetails,
	});
	const hasAcronis = Boolean(
		backupsAndStorage && 'acronis' in backupsAndStorage,
	);
	const hasBlockStorage = Boolean(backupsAndStorage?.block_storage?.length);
	const isBareMetal = categories?.includes('BareMetalMAAS');
	const isBeyondHosting = categories?.includes('BeyondHostingVPS');
	const isChild = Boolean(privateParent);
	const isMWPv1 = type === 'WP.VM';
	const isOpenStack = categories?.includes('NocworxOpenstack');
	const isPrivateParent = type === 'SS.PP';
	const isStorm = getIsStorm(type);
	const isVmware = type === 'VMware.vCloud.vDC';
	const location = regionId ? dataCenterMap[regionId]?.label : undefined;
	const managementLevel =
		template?.management ||
		featureDetails?.Template?.support_level ||
		featureDetails?.ControlPanel?.description ||
		featureDetails?.CloudManagement?.description;
	const mesRole = mesDetails?.mes_role;
	const mesType = mesDetails?.mes_type;
	const parentDiskResources = getParentDiskResources({
		resourceState,
		diskDetails,
	});
	const parentMemoryResources = getParentMemoryResources(resourceState);
	const privateIp = networkSummary?.private_ip;
	const processorSpeed = getProcessorSpeed({ machine });
	const raidLevelFormatted = `RAID${machine?.raid_level}`;
	const zoneFormatted = zone?.name
		? `${zone?.region.name} — ${zone?.name}`
		: undefined;

	const backupPlan = getBackupPlan(hasAcronis, featureDetails || {});
	const backupsState = getBackupsState(canAcronisBackup, backupPlan);
	const cores = getCores({
		featureDetails,
		isPrivateParent,
		nocworxRemoteDetails,
		machine,
	});
	const cpu = getCpu({
		assetType,
		featureDetails,
		isBeyondHosting,
		isChild,
		isPrivateParent,
		isVmware,
		machine,
	});
	const displayType = altGroup || displayName(assetType, type);
	const gpu = getGpu({
		assetType,
		machine,
	});
	const linkSpeed = getLinkSpeed({
		assetType,
		machine,
	});
	const os =
		template?.os_type ||
		getOS({
			assetType,
			featureDetails,
			nocworxRemoteDetails,
			isBeyondHosting,
			machine,
		});
	const powerStatus = isBeyondHosting
		? getBeyondHostingPowerStatus(nocworxRemoteDetails?.power_status || '')
		: powerStatusRaw;
	const primaryStorage = getPrimaryStorage({
		assetType,
		featureDetails,
		isBeyondHosting,
		nocworxRemoteDetails,
		machine,
	});
	const processor = getProcessor({
		assetType,
		featureDetails,
		machine,
	});
	const ram = getRam({
		assetType,
		featureDetails,
		isBeyondHosting,
		isChild,
		isPrivateParent,
		machine,
	});

	const isWindows = os.toLowerCase().includes('windows');
	const osFormatted = template
		? `${stripOsName(template.os_name || '')} ${template.os_version}, ${template.os_bit}-bit`
		: os;
	const ramFormatted = getRamFormatted(ram);

	return {
		...data,
		acronisId,
		acronisStatus,
		assetType,
		availabilityZoneId,
		backupPlan,
		backupQuotaValue,
		backupsState,
		bandwidthInGB,
		canBackupsAndImages,
		canConsole,
		controlPanel,
		cores,
		country,
		cpanelLicense,
		cpu,
		disableConsole,
		displayType,
		gpu,
		hardDrives,
		hasAcronis,
		hasBlockStorage,
		isBareMetal,
		isBeyondHosting,
		isMWPv1,
		isOpenStack,
		isPrivateParent,
		isStorm,
		isVmware,
		isWindows,
		linkSpeed,
		location,
		managementLevel,
		mesRole,
		mesType,
		os,
		osFormatted,
		parentDiskResources,
		parentMemoryResources,
		powerStatus,
		primaryStorage,
		privateIp,
		processor,
		processorSpeed,
		raidLevelFormatted,
		ram,
		ramFormatted,
		zoneFormatted,
	};
};

/**
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsParamsRawI} params
 * @param {object} [options]
 * @param {import('@tanstack/react-query').UseQueryOptions['enabled']} [options.enabled]
 */
const useAssetDetails = (params, { enabled } = {}) =>
	useQuery({
		queryKey: ['/asset/details', params],
		queryFn: () => api.asset.apiRaw.details(params),
		enabled,
		select: supplementDetails,
	});

/**
 * @param {import('club-sauce/public/asset/index.raw').LWApiPublicAssetDetailsParamsRawI} params
 */
export const usePollAssetDetails = (params) =>
	useQuery({
		queryKey: ['/asset/details', params],
		queryFn: () => api.asset.apiRaw.details(params),
		refetchInterval: 30000,
		select: supplementDetails,
	});

export default useAssetDetails;
